import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { submitFeedback } from '../../services/feedbackService';

const TranslationFeedback = ({
    translationId,
    onFeedbackSubmit,
    onHide,
    user: userProp,
    input,
    output,
    fromLanguage,
    toLanguage,
    apiEndpoint
}) => {
    const [feedbackType, setFeedbackType] = useState(null); // 'positive' or 'negative'
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [feedbackText, setFeedbackText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Get user from Redux store if not provided as prop
    const reduxUser = useSelector((state) => state.auth.user);
    const user = userProp || reduxUser;

    const feedbackOptions = [
        { id: 'incorrect', label: 'The meaning of the translation is incorrect' },
        { id: 'missing', label: 'Parts of the text were missing or not translated' },
        { id: 'unnatural', label: 'The phrasing sounds unnatural or awkward' },
        { id: 'cultural', label: 'Cultural or local context was not respected' },
        { id: 'other', label: 'Other' },
    ];

    const handlePositiveFeedback = async () => {
        const feedbackData = {
            translationId,
            type: 'positive',
            input: input || '',
            output: output || '',
            fromLanguage: fromLanguage || '',
            toLanguage: toLanguage || '',
            api_endpoint: apiEndpoint || '/translate/with-api-key',
            feedback_text: 'Liked translation',
            is_liked: true,
            timestamp: new Date().toISOString(),
        };

        setSubmitting(true);
        try {
            // Prepare user info for API
            const userInfo = {};
            if (user?.id) {
                userInfo.user_id = user.id;
            }
            if (user?.email) {
                userInfo.email = user.email;
            }
            if (user?.firstName || user?.lastName) {
                userInfo.full_name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
            }

            // Submit feedback to API
            await submitFeedback(feedbackData, userInfo);

            // Call the parent callback if provided
            if (onFeedbackSubmit) {
                await onFeedbackSubmit(feedbackData);
            }

            toast.success('Thank you for your feedback!');

            // Hide the feedback card after successful submission
            if (onHide) {
                onHide();
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
            toast.error('Failed to submit feedback. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleNegativeFeedback = () => {
        setFeedbackType('negative');
    };

    const handleSubmit = async () => {
        if (!selectedIssue) {
            toast.error('Please select an issue type');
            return;
        }

        if (selectedIssue === 'other' && !feedbackText.trim()) {
            toast.error('Please provide details for "Other"');
            return;
        }

        // Get the issue label for feedback_text
        const issueLabel = feedbackOptions.find(opt => opt.id === selectedIssue)?.label || selectedIssue;
        const feedbackTextValue = selectedIssue === 'other'
            ? feedbackText.trim()
            : issueLabel + (feedbackText.trim() ? `: ${feedbackText.trim()}` : '');

        const feedbackData = {
            translationId,
            type: 'negative',
            input: input || '',
            output: output || '',
            fromLanguage: fromLanguage || '',
            toLanguage: toLanguage || '',
            api_endpoint: apiEndpoint || '/translate/with-api-key',
            feedback_text: feedbackTextValue,
            is_liked: false,
            issue: selectedIssue,
            comment: feedbackText.trim() || null,
            timestamp: new Date().toISOString(),
        };

        setSubmitting(true);
        try {
            // Prepare user info for API
            const userInfo = {};
            if (user?.id) {
                userInfo.user_id = user.id;
            }
            if (user?.email) {
                userInfo.email = user.email;
            }
            if (user?.firstName || user?.lastName) {
                userInfo.full_name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
            }

            // Submit feedback to API
            await submitFeedback(feedbackData, userInfo);

            // Call the parent callback if provided
            if (onFeedbackSubmit) {
                await onFeedbackSubmit(feedbackData);
            }

            toast.success('Thank you for your feedback!');

            // Hide the feedback card after successful submission
            if (onHide) {
                onHide();
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
            toast.error('Failed to submit feedback. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">How was this translation?</h3>
                    <p className="text-sm text-gray-500">
                        {feedbackType === 'negative'
                            ? 'Help us understand what went wrong'
                            : 'Your feedback helps us improve'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={handlePositiveFeedback}
                        disabled={submitting}
                        className="p-2 rounded-lg border border-gray-300 text-gray-400 hover:border-green-500 hover:bg-green-50 hover:text-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Good translation"
                    >
                        <ThumbsUp className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleNegativeFeedback}
                        disabled={submitting}
                        className="p-2 rounded-lg border border-gray-300 text-gray-400 hover:border-red-500 hover:bg-red-50 hover:text-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Bad translation"
                    >
                        <ThumbsDown className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {feedbackType === 'negative' && (
                <>
                    <div className="space-y-3 mb-4">
                        {feedbackOptions.map((option) => (
                            <label
                                key={option.id}
                                className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                                <span className="text-sm text-gray-700">{option.label}</span>
                                <input
                                    type="radio"
                                    name="feedback-issue"
                                    value={option.id}
                                    checked={selectedIssue === option.id}
                                    onChange={(e) => setSelectedIssue(e.target.value)}
                                    className="w-5 h-5 accent-secondary focus:ring-secondary cursor-pointer"
                                />
                            </label>
                        ))}
                    </div>

                    {selectedIssue === 'other' && (
                        <textarea
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="Tell us what could be improved"
                            className="w-full p-3 border border-gray-200 rounded-lg resize-none outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-sm mb-4"
                            rows={3}
                        />
                    )}

                    <button
                        onClick={handleSubmit}
                        disabled={!selectedIssue || (selectedIssue === 'other' && !feedbackText.trim()) || submitting}
                        className="w-full bg-secondary hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
                    >
                        {submitting ? 'Submitting...' : 'Submit Feedback'}
                    </button>
                </>
            )}
        </div>
    );
};

export default TranslationFeedback;
